/**
 * apiClient - shared HTTP client built on the native `fetch` API.
 *
 * IMPORTANT: This project uses native `fetch` only — never axios or any other
 * HTTP-client library. All frontend HTTP requests go through this module.
 *
 * Public interface (kept intentionally axios-like so service modules read the same):
 *   apiClient.get(url, { params })
 *   apiClient.post(url, body)
 *   apiClient.put(url, body)
 *   apiClient.del(url, body)
 *
 * Each method resolves to the parsed JSON response body directly (not a wrapper
 * object). On a non-2xx response it throws an Error carrying `.status` and `.data`
 * (the parsed error body, when present).
 */

const BASE_URL = '/';

/** Build a query string from a params object, skipping null/undefined/empty values. */
function buildQueryString(params) {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.append(key, value);
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** Prepend the base URL to a root-relative path. */
function resolveUrl(url, params) {
  const base = url.startsWith('/') ? `${BASE_URL.replace(/\/$/, '')}${url}` : url;
  return `${base}${buildQueryString(params)}`;
}

/** Parse a response body as JSON, tolerating empty/204 responses. */
async function parseBody(response) {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // Non-JSON body (e.g. plain-text error); return the raw text.
    return text;
  }
}

async function request(method, url, { params, body } = {}) {
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const options = {
    method,
    credentials: 'include', // cookie/session auth
    headers: {}
  };

  if (body !== undefined && body !== null) {
    if (isFormData) {
      // Let the browser set the multipart boundary Content-Type.
      options.body = body;
    } else {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
  }

  const response = await fetch(resolveUrl(url, params), options);

  if (!response.ok) {
    if (response.status === 401 && !url.includes('/api/me')) {
      // /api/me returns 401 for unauthenticated users without redirecting.
      window.location.href = '/login';
    }
    const data = await parseBody(response);
    const error = new Error(`Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return parseBody(response);
}

const apiClient = {
  get: (url, config = {}) => request('GET', url, { params: config.params }),
  post: (url, body) => request('POST', url, { body }),
  put: (url, body) => request('PUT', url, { body }),
  del: (url, body) => request('DELETE', url, { body })
};

export default apiClient;
